// DeploymentRequestsPage — every deployment a customer has asked us to build.
//
// The screen that did not exist, and its absence was the whole of the gap. A
// customer could file a deployment request and nobody at metricsIQ could see
// it: the staff surface read customers, subscriptions, instances, usage,
// submissions and alerts, and never provisioning requests. The only way to
// reach one was `manage.py standup_plan <public_id>` — and nothing could list
// them, so the identifier had to come from somewhere else entirely.
//
// WAITING FIRST, then the record. A request that has been settled is how the
// instance beside it came to exist, so it stays on the screen rather than
// disappearing — but an operator opening this page is here to find work, and
// the work is the unsettled rows. Splitting them is the difference between a
// queue and an archive, and this page has to be both.
//
// Standing a deployment up is still people's work: vend the account, then
// deploy the instance. Nothing here dispatches anything, and the page says so
// rather than implying a button exists somewhere.
//
// Moments are rendered from their strings without becoming numbers — see
// data/format.ts.

import { Fragment, useCallback, useState, type FormEvent } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import { Button } from '@miq/design-system/components/ui/button';
import { Input } from '@miq/design-system/components/ui/input';

import { CopyableValue } from '../components/CopyableValue';
import { RetiredBadge } from '../components/RetiredBadge';
import { ScreenFailed, ScreenLoading } from '../components/ScreenState';
import { ShowFormerToggle } from '../components/ShowFormerToggle';
import { PortalRequestFailed, type AuthorizedFetch } from '../auth/api';
import { usePortalSession } from '../auth/context';
import { when } from '../data/instants';
import {
  bindDeploymentRequest,
  declineDeploymentRequest,
  readDeploymentRequests,
  type DeploymentActRecorded,
  type DeploymentRequest,
} from '../data/acrossCustomers';
import { usePortalRead } from '../data/usePortalRead';
import { useDocumentTitle } from '../../lib/documentTitle';

const TITLE = 'Deployment Requests';

/** Waiting on us, in the order they arrived — oldest first. */
function stillWaiting(requests: DeploymentRequest[]): DeploymentRequest[] {
  // Oldest first, deliberately against the endpoint's newest-first order:
  // a queue is worked from the front, and the request that has been waiting
  // longest is the one most likely to be somebody wondering where we are.
  return requests
    .filter((request) => request.settled_at === null)
    .slice()
    .reverse();
}

function settled(requests: DeploymentRequest[]): DeploymentRequest[] {
  return requests.filter((request) => request.settled_at !== null);
}

/**
 * The two things an operator can do about a waiting request.
 *
 * Opened one row at a time and closed on success, rather than sitting open
 * beside every row: these are the only destructive affordances on metricsIQ's
 * screens, and a page of always-visible Decline buttons is a page where one
 * gets pressed by the wrong hand.
 *
 * A REASON IS REQUIRED for both, and required by the server as well — this is
 * not client-side politeness. These acts exist precisely because something
 * happened that the service could not observe for itself, so the operator's
 * sentence is the only account of what that was, and a blank one would leave
 * an audit row that records who and when and nothing about why.
 */
function ActOnRequest({
  request,
  act,
  onRecorded,
  onCancel,
}: {
  request: DeploymentRequest;
  act: 'decline' | 'bind';
  onRecorded: (recorded: DeploymentActRecorded) => void;
  onCancel: () => void;
}) {
  const { authorizedFetch } = usePortalSession();
  const [reason, setReason] = useState('');
  const [name, setName] = useState(request.deployment_name);
  const [refusal, setRefusal] = useState('');
  const [working, setWorking] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setRefusal('');
    setWorking(true);
    try {
      const recorded =
        act === 'decline'
          ? await declineDeploymentRequest(
              authorizedFetch,
              request.public_id,
              reason,
            )
          : await bindDeploymentRequest(
              authorizedFetch,
              request.public_id,
              name,
              reason,
            );
      onRecorded(recorded);
    } catch (failure) {
      // The service's own wording, shown as-is. A 409 here is not a fault:
      // it is the service declining to do something that no longer makes
      // sense — a request already answered, a deployment that has already
      // reported in — and the operator needs to read that sentence, not a
      // generic failure.
      setRefusal(
        failure instanceof PortalRequestFailed
          ? failure.message
          : 'That could not be recorded. Check your connection and try again.',
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2 py-2">
      {act === 'bind' ? (
        <div>
          <label className="text-xs text-muted-foreground" htmlFor="ns">
            The namespace you vended under. Lower-case, digits and single
            hyphens — this is what both platform workflows key on.
          </label>
          <Input
            id="ns"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-1 font-mono text-xs"
          />
        </div>
      ) : null}
      <div>
        <label className="text-xs text-muted-foreground" htmlFor="why">
          Why. Recorded verbatim against your name — this is the only account
          of what happened.
        </label>
        <Input
          id="why"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="mt-1"
        />
      </div>
      {refusal ? (
        <p className="text-xs font-medium text-destructive">{refusal}</p>
      ) : null}
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          variant={act === 'decline' ? 'destructive' : 'default'}
          isPending={working}
          isDisabled={!reason.trim() || (act === 'bind' && !name.trim())}
        >
          {act === 'decline' ? 'Decline this request' : 'Bind to this name'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onPress={onCancel}
          isDisabled={working}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function RequestTable({
  requests,
  showSettled,
  mayAct = false,
  acting,
  onOpen,
  onRecorded,
}: {
  requests: DeploymentRequest[];
  showSettled: boolean;
  mayAct?: boolean;
  acting?: { request: string; act: 'decline' | 'bind' } | null;
  onOpen?: (request: string, act: 'decline' | 'bind' | null) => void;
  onRecorded?: (request: string, recorded: DeploymentActRecorded) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Deployment</TableHead>
            {/* Before the pack and the region, because it is the field an
                operator has to carry off this screen: the platform deploy
                takes it as an input and creates that login. */}
            <TableHead>First login</TableHead>
            <TableHead>Decision pack</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Asked by</TableHead>
            <TableHead>{showSettled ? 'Settled' : 'Waiting since'}</TableHead>
            <TableHead>State</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.map((request) => (
            // Keyed on the FRAGMENT, not on the rows inside it: a
            // request draws two rows when it carries a note, and keying
            // the children instead leaves the fragment itself unkeyed.
            <Fragment key={request.public_id}>
              <TableRow>
                <TableCell className="font-medium">
                  {request.customer}
                  {/* The customer's retirement, not the request's: a retired
                      customer's requests are history the way its instances
                      are, and only ever on the screen under "Show former
                      customers". */}
                  {request.retired !== null ? (
                    <div className="mt-1">
                      <RetiredBadge retired={request.retired} />
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {/* Both names, because they are two different facts and
                      only one of them is the identifier that matters.

                      The first is what the customer typed. The second is
                      what it reduced to, and it is the namespace an operator
                      must vend and deploy under: the deployment reports in
                      under that name or it is never heard from, and the
                      request then sits at Requested forever with nothing
                      anywhere saying why. Showing only the prose is how an
                      operator comes to vend `mktest2-dev` for a request that
                      is waiting on `marketplace-test-two`. */}
                  {request.instance_name}
                  {request.deployment_name ? (
                    <div className="mt-0.5 text-muted-foreground">
                      deploy as{' '}
                      <CopyableValue
                        value={request.deployment_name}
                        label="deployment name"
                      />
                    </div>
                  ) : null}
                </TableCell>
                <TableCell>
                  {/* Copyable rather than only selectable: this address is
                      retyped into the deploy by hand, and a typo in it is an
                      instance nobody can log into — found days later, by the
                      person who cannot get in.

 "Not asked" rather than a dash, and never the address
                      beside it: a request filed before the form began asking
                      names nobody, and an operator working one of those has
                      to go and ask rather than assume the buyer meant
                      themselves. Assuming is how an instance came to be
                      seeded with an address nobody had chosen. */}
                  <CopyableValue
                    value={request.initial_user_email}
                    label="first login address"
                    absent="not asked"
                  />
                </TableCell>
                <TableCell>{request.decision_pack}</TableCell>
                <TableCell>{request.region}</TableCell>
                <TableCell className="text-muted-foreground">
                  {request.requested_by || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {when(
                    showSettled
                      ? (request.settled_at as string)
                      : request.requested_at,
                  )}
                </TableCell>
                <TableCell>
                  {request.state}
                  {/* A request that has been waiting past a plausible
                      deploy says so. Not a claim that the deploy failed —
                      a deploy that fails never boots, so it never
                      authenticates, and nothing in this service can tell
                      that from one still running. What it stops is the
                      silence reading as a slow success. */}
                  {request.overdue ? (
                    <div className="mt-0.5 text-xs font-medium text-warning">
                      no word from the deployment
                    </div>
                  ) : null}
                </TableCell>
              </TableRow>
              {!showSettled &&
              request.deployments_no_request_asked_for.length > 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="pt-0 text-sm">
                    {/* The signature of a correlation that went wrong. A
                        live deployment under this subscription that no
                        request created, while this request is still
                        waiting, is almost always one deployment and one
                        request that failed to find each other.

                        Named, never matched: pairing them here would settle
                        a request on a resemblance of spelling, which is the
                        bug. An operator who can see both makes the call. */}
                    <span className="font-medium text-warning">
                      Also running under this subscription, asked for by no
                      request:{' '}
                    </span>
                    <span className="font-mono text-xs">
                      {request.deployments_no_request_asked_for.join(', ')}
                    </span>
                  </TableCell>
                </TableRow>
              ) : null}
              {mayAct && !showSettled ? (
                <TableRow>
                  <TableCell colSpan={8} className="pt-0">
                    {acting?.request === request.public_id ? (
                      <ActOnRequest
                        request={request}
                        act={acting.act}
                        onRecorded={(recorded) => {
                          onRecorded?.(request.public_id, recorded);
                          onOpen?.(request.public_id, null);
                        }}
                        onCancel={() => onOpen?.(request.public_id, null)}
                      />
                    ) : (
                      <div className="flex gap-2">
                        {/* Bind first, and Decline last. The common act is
                            saying which namespace this was vended under; the
                            rare one ends the request. Ordering them the other
                            way would put the destructive button where the
                            hand goes by default. */}
                        <Button
                          size="sm"
                          variant="outline"
                          onPress={() => onOpen?.(request.public_id, 'bind')}
                        >
                          Bind to a namespace
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onPress={() => onOpen?.(request.public_id, 'decline')}
                        >
                          Decline
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ) : null}
              {request.note ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="pt-0 text-sm text-muted-foreground"
                  >
                    {/* Whatever else they told us. Shown in full rather
                        than truncated: it is the only free text on the
                        request and it is where the awkward constraint
                        usually is. */}
                    “{request.note}”
                  </TableCell>
                </TableRow>
              ) : null}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function DeploymentRequestsPage() {
  useDocumentTitle(TITLE);
  // Requests of former customers — retired or superseded — are hidden by
  // default and come back under the toggle. Memoised per setting because
  // `usePortalRead` holds the read in a dependency array; flipping the toggle
  // is when it should re-run.
  const [showFormer, setShowFormer] = useState(false);
  const read = useCallback(
    (authorizedFetch: AuthorizedFetch) => readDeploymentRequests(authorizedFetch, showFormer),
    [showFormer],
  );
  const screen = usePortalRead(read);
  const [acting, setActing] = useState<{
    request: string;
    act: 'decline' | 'bind';
  } | null>(null);
  // What the SERVICE said it recorded, laid over the rows this screen read
  // on mount. `usePortalRead` reads once and does not refetch, and adding a
  // refetch to it for this one page would make every screen in the portal
  // re-request on an unrelated schedule. The endpoints echo back the fields
  // they changed precisely so this overlay can be the service's own answer
  // rather than an optimistic guess about what it probably did.
  const [recorded, setRecorded] = useState<
    Record<string, DeploymentActRecorded>
  >({});

  if (screen.status === 'loading') {
    return <ScreenLoading title={TITLE} />;
  }
  if (screen.status === 'failed') {
    // Includes the 404 this endpoint answers a caller who may not look. It
    // is a refusal, not a missing page.
    return <ScreenFailed title={TITLE} message={screen.message} />;
  }

  const requests = screen.data.requests.map((request) => {
    const answer = recorded[request.public_id];
    if (!answer) {
      return request;
    }
    return {
      ...request,
      state: answer.state ?? request.state,
      settled_at: answer.settled_at ?? request.settled_at,
      deployment_name: answer.deployment_name ?? request.deployment_name,
    };
  });
  const waiting = stillWaiting(requests);
  const done = settled(requests);
  const mayAct = screen.data.may_act;
  // Once shown, the count is of the rows the row itself can mark: a request
  // carries its customer's retirement but not a supersession, so a superseded
  // customer's requests are in the list yet not in this number. The service's
  // count, used while they are hidden, includes both.
  const hiddenCount = showFormer
    ? requests.filter((request) => request.retired !== null).length
    : screen.data.former_hidden;
  const openAct = (request: string, act: 'decline' | 'bind' | null) =>
    setActing(act === null ? null : { request, act });
  const remember = (request: string, answer: DeploymentActRecorded) =>
    setRecorded((known) => ({ ...known, [request]: answer }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{TITLE}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            What customers have asked us to stand up, across every customer.
            Standing one up is still people’s work — vend the account, deploy the
            instance, and hand the platform the first login the customer named —
            and nothing on this screen dispatches anything.
            {mayAct
              ? ' What it can do is record what you did about a request: bind one to the namespace you vended under, so the deployment and the request can find each other, or decline one that is not going to be built. Both ask for a reason and keep it against your name.'
              : ' Recording an answer to a request needs the act-for-customers role, which this sign-in does not hold.'}
          </p>
        </div>
        {/* A former customer's requests leave this screen with the
            customer; the toggle brings them back as history. */}
        <ShowFormerToggle
          showing={showFormer}
          hiddenCount={hiddenCount}
          onChange={setShowFormer}
          of="customers"
        />
      </div>

      <Card
        className={
          waiting.length > 0 ? 'border-warning/30 bg-warning-soft' : undefined
        }
      >
        <CardHeader>
          <CardTitle className="text-base">
            {waiting.length === 0
              ? 'Nothing waiting'
              : waiting.length === 1
                ? '1 request waiting'
                : `${waiting.length} requests waiting`}
          </CardTitle>
          <CardDescription>
            {waiting.length === 0
              ? 'Every deployment that has been asked for has been answered.'
              : 'Oldest first. A request is answered by settling it, not by replying to the mail.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {waiting.length === 0 ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              Nothing to build.
            </p>
          ) : (
            <RequestTable
              requests={waiting}
              showSettled={false}
              mayAct={mayAct}
              acting={acting}
              onOpen={openAct}
              onRecorded={remember}
            />
          )}
        </CardContent>
      </Card>

      {done.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Already answered ({done.length})
            </CardTitle>
            <CardDescription>
              Kept rather than cleared: a settled request is how the instance
              beside it came to exist, and the region it names is the only
              record of where the customer asked for it to run.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <RequestTable requests={done} showSettled />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
